import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedModule } from "../../../../shared/shared.module";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { NC_CLOSURE_METRICS_DATA } from "../../models/data.model";
import { exportNcClosureMetricsExcel } from "./nc-closure-metrics-excel.utils";
import {
  ExcelExportConfirmDialogComponent,
  ExcelExportConfirmData,
  ExcelExportConfirmResult,
} from "../../../../shared/components/excel-export-confirm-dialog/excel-export-confirm-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { NonConformiteApiService } from "../../../non-conformite/services/non-conformite/non-conformite-api.service";

export interface NcClosureMetricsDialogData {
  data: NC_CLOSURE_METRICS_DATA;
  start?: string;
  end?: string;
}

const DECISION_LABEL_MAP: Record<string, string> = {
  ACCEPTATION_DEROGATION: "dashboard_ui.nc_decision_acceptation_derogation",
  RETOUCHE: "dashboard_ui.nc_decision_retouche",
  REBUT: "dashboard_ui.nc_decision_rebut",
  RETOUR_FOURNISSEUR: "dashboard_ui.nc_decision_retour_fournisseur",
  NON_RENSEIGNEE: "dashboard_ui.nc_decision_non_renseignee",
};

@Component({
  selector: "vl-nc-closure-metrics-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    DecimalPipe,
    ExcelExportButtonComponent,
    RadioCardComponent,
  ],
  templateUrl: "./nc-closure-metrics-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NcClosureMetricsDialogComponent {
  data = inject<NcClosureMetricsDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<NcClosureMetricsDialogComponent>);

  exporting = signal(false);

  timeline = computed(() => this.data.data?.timeline ?? []);
  decisions = computed(() =>
    (this.data.data?.delaiParDecision ?? []).map((d) => ({
      ...d,
      i18nKey: DECISION_LABEL_MAP[d.decision] ?? d.decision,
    })),
  );

  /** Total clôturées par décision pour barres relatives. */
  maxDecisionCount = computed(() =>
    this.decisions().reduce((m, d) => Math.max(m, d.count), 0),
  );

  /** Max NC mensuelles (ouvertes + clôturées) pour barres timeline. */
  maxMonthCount = computed(() =>
    this.timeline().reduce((m, t) => Math.max(m, t.ouvertes + t.cloturees), 0),
  );

  decisionBarWidth(count: number): string {
    const max = this.maxDecisionCount();
    return max > 0 ? `${(count / max) * 100}%` : "0%";
  }

  monthClotureesWidth(count: number): string {
    const max = this.maxMonthCount();
    return max > 0 ? `${(count / max) * 100}%` : "0%";
  }

  monthOuvertesWidth(count: number): string {
    const max = this.maxMonthCount();
    return max > 0 ? `${(count / max) * 100}%` : "0%";
  }

  fmtDelai(v: number | null): string {
    return v == null ? "—" : `${v} j`;
  }

  fmtPct(v: number | null): string {
    return v == null ? "—" : `${v}%`;
  }

  private dialogs = inject(DialogsService);
  private ncApi = inject(NonConformiteApiService);

  async exportExcel() {
    const ref = this.dialogs.open(
      ExcelExportConfirmDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Exporter — Métriques de clôture NC",
        subtitle:
          this.data.start && this.data.end
            ? `Plage active : ${this.fmtPeriod(this.data.start)} → ${this.fmtPeriod(this.data.end)}`
            : undefined,
        allowCustomRange: true,
        initialCustomStart: this.data.start,
        initialCustomEnd: this.data.end,
      } as ExcelExportConfirmData,
    );
    const confirm = (await firstValueFrom(ref.afterClosed())) as
      | ExcelExportConfirmResult
      | null;
    if (!confirm) return;

    this.exporting.set(true);
    try {
      let dataToExport = this.data.data;
      if (confirm.customStart && confirm.customEnd) {
        // Recharge avec la plage personnalisée demandée
        dataToExport = await firstValueFrom(
          this.ncApi.closureMetrics(confirm.customStart, confirm.customEnd),
        );
      }
      await exportNcClosureMetricsExcel(dataToExport);
    } finally {
      this.exporting.set(false);
    }
  }

  private fmtPeriod(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return iso;
    }
  }
}
